import {
  argbFromHex,
  argbFromRgb,
  Blend,
  blueFromArgb,
  greenFromArgb,
  hexFromArgb,
  redFromArgb,
} from '@material/material-color-utilities';
import {css} from 'styled-components';

import Luiv3Constants from '../../lui3/constants';
import {
  colorDarkTone,
  colorLightTone,
  containerDarkTone,
  containerLightTone,
  darkFromCorePalette,
  lightFromCorePalette,
  makePalette,
  onColorDarkTone,
  onColorLightTone,
  onContainerDarkTone,
  onContainerLightTone,
} from './scheme';
import {ALL_TINTS, Elevation} from './tints';

const customColorDefs = [
  {
    name: 'pink',
    value: argbFromHex(Luiv3Constants.baseColor['pink-400']),
    blend: false,
    lightToneMod: 0,
    invert: false,
    forceColor: true,
  } as const,
  {
    name: 'purple',
    value: argbFromHex(Luiv3Constants.baseColor['purple-500']),
    onValue: argbFromHex(Luiv3Constants.baseColor['gray-0']),
    blend: false,
    forceColor: true,
  } as const,
  {
    name: 'success',
    value: argbFromHex(Luiv3Constants.baseColor['green-600']),
    onValue: argbFromHex(Luiv3Constants.baseColor['gray-0']),
    blend: false,
    forceColor: true,
  } as const,
  {
    name: 'warn',
    value: argbFromHex(Luiv3Constants.baseColor['yellow-500']),
    blend: false,
    forceColor: true,
  } as const,
  {
    name: 'yellow',
    value: argbFromHex(Luiv3Constants.baseColor['yellow-500']),
    blend: false,
    forceColor: true,
  } as const,
  {
    name: 'orange',
    value: argbFromHex(Luiv3Constants.baseColor['orange-500']),
    blend: false,
    forceColor: true,
  } as const,
  {
    name: 'copilot',
    value: argbFromHex(Luiv3Constants.baseColor['purple-500']),
    onValue: argbFromHex(Luiv3Constants.baseColor['gray-0']),
    blend: false,
    forceColor: true,
  } as const,
  {
    name: 'secondarySurface',
    value: argbFromHex(Luiv3Constants.baseColor['gray-37-5']),
    onValue: argbFromHex(Luiv3Constants.baseColor['gray-700']),
    blend: false,
    forceColor: true,
  } as const,
];

const primary = argbFromHex(Luiv3Constants.baseColor['blue-500']);

const capitalize = <T extends string>(s: T) =>
  (s[0].toUpperCase() + s.substring(1)) as Capitalize<T>;

const overlay = (base: number, overlay: number, a: number) => {
  const r = a * redFromArgb(overlay) + (1 - a) * redFromArgb(base);
  const g = a * greenFromArgb(overlay) + (1 - a) * greenFromArgb(base);
  const b = a * blueFromArgb(overlay) + (1 - a) * blueFromArgb(base);
  return argbFromRgb(r, g, b);
};

export const getThemeColors = (dark?: boolean) => {
  const palette = makePalette(primary);

  const scheme = dark
    ? darkFromCorePalette(palette)
    : lightFromCorePalette(palette);

  const baseColors = Object.entries(scheme) as [keyof typeof scheme, number][];

  const customColors = customColorDefs
    .map(
      ({
        blend,
        name,
        value,
        invert,
        onValue,
        lightToneMod = 0,
        forceColor = false,
      }) => {
        if (blend) {
          value = Blend.harmonize(value, primary);
        }

        const palette = makePalette(value);
        const tones = palette.a1;

        if (forceColor) {
          return {
            [`${name}`]: value,
            [`on${capitalize(name)}`]: onValue,
            [`${name}Container`]: value,
            [`on${capitalize(name)}Container`]: onValue,
          };
        }
        if (!!dark === !!invert) {
          return {
            [`${name}`]: tones.tone(colorLightTone + lightToneMod),
            [`on${capitalize(name)}`]: tones.tone(onColorLightTone),
            [`${name}Container`]: tones.tone(containerLightTone),
            [`on${capitalize(name)}Container`]:
              tones.tone(onContainerLightTone),
          };
        } else {
          return {
            [`${name}`]: tones.tone(colorDarkTone),
            [`on${capitalize(name)}`]: tones.tone(onColorDarkTone),
            [`${name}Container`]: tones.tone(containerDarkTone),
            [`on${capitalize(name)}Container`]: tones.tone(onContainerDarkTone),
          };
        }
      }
    )
    .flatMap((color) => Object.entries(color) as [CustomColor, number][]);

  return [...baseColors, ...customColors] satisfies [Color, number][];
};

export const getThemeColorVars = (dark?: boolean) => {
  const allColors = getThemeColors(dark);
  const allColorsMap = new Map(allColors);

  const themeColors = allColors
    .map(([key, value]) => `--theme-color-${key}: ${hexFromArgb(value)};`)
    .join('');

  const themeElevatedColors = allColors
    .flatMap(([key, value]) => {
      if (!isBackgroundColor(key)) {
        return [];
      }
      return ALL_TINTS.map(
        ([tintName, tintValue]) =>
          `--theme-color-${key}-${tintName}: ${hexFromArgb(
            overlay(value, allColorsMap.get(getOnColor(key)) ?? 0, tintValue)
          )};`
      );
    })
    .join('');

  // TODO DO NOT USE TRANSITION TO NEW COLORS
  const legacyColors = css`
    --theme-surface-color: ${Luiv3Constants.baseColor['gray-0']};
    --theme-surface-highlight: ${Luiv3Constants.baseColor['gray-100']};
    --theme-canvas-color: ${Luiv3Constants.baseColor['gray-37-5']};
    --theme-canvas-highlight: ${Luiv3Constants.baseColor['gray-100']};
    --theme-canvas-2-color: ${Luiv3Constants.baseColor['gray-100']};

    --theme-on-surface-color: ${Luiv3Constants.baseColor['gray-700']};
    --theme-on-surface-color-bold: var(--theme-on-surface-color);
    --theme-on-canvas-color: var(--theme-on-surface-color);
    --theme-on-canvas-color-bold: var(--theme-on-surface-color);

    --theme-primary-color: ${Luiv3Constants.baseColor['blue-500']};
    --theme-primary-highlight: ${Luiv3Constants.baseColor['blue-500']};
    --theme-on-primary-color: ${Luiv3Constants.baseColor['gray-0']};
    --theme-on-primary-color-bold: var(--theme-on-primary-color);
    --theme-secondary-color: ${Luiv3Constants.baseColor['gray-200']};
    --theme-secondary-highlight: ${Luiv3Constants.baseColor['gray-200']};
    --theme-on-secondary-color: ${Luiv3Constants.baseColor['gray-600']};
    --theme-on-secondary-color-bold: var(--theme-on-secondary-color);
    --theme-contrast-color: ${Luiv3Constants.baseColor['gray-900']};
    --theme-contrast-highlight: ${Luiv3Constants.baseColor['blue-900']};
    --theme-on-contrast-color: ${Luiv3Constants.baseColor['blue-50']};
    --theme-on-contrast-color-bold: ${Luiv3Constants.baseColor['gray-0']};
    --theme-info-color: var(--theme-primary-color);
    --theme-info-highlight: var(--theme-primary-highlight);
    --theme-on-info-color: var(--theme-on-primary-color);
    --theme-on-info-color-bold: var(--theme-on-primary-color-bold);
    --theme-danger-color: ${Luiv3Constants.baseColor['red-500']};
    --theme-danger-highlight: ${Luiv3Constants.baseColor['red-500']};
    --theme-on-danger-color: ${Luiv3Constants.baseColor['gray-0']};
    --theme-on-danger-color-bold: var(--theme-on-danger-color);
    --theme-success-color: ${Luiv3Constants.baseColor['green-500']};
    --theme-success-highlight: ${Luiv3Constants.baseColor['green-500']};
    --theme-on-success-color: ${Luiv3Constants.baseColor['gray-0']};
    --theme-on-success-color-bold: var(--theme-on-success-color);
    --theme-warn-color: ${Luiv3Constants.baseColor['orange-300']};
    --theme-warn-highlight: ${Luiv3Constants.baseColor['orange-500']};
    --theme-on-warn-color: ${Luiv3Constants.baseColor['orange-900']};
    --theme-on-warn-color-bold: var(--theme-on-warn-color);
    --theme-paywall-color: ${Luiv3Constants.baseColor['orange-500']};
    --theme-paywall-highlight: ${Luiv3Constants.baseColor['orange-300']};
    --theme-on-paywall-color: ${Luiv3Constants.baseColor['gray-0']};
    --theme-on-paywall-color-bold: var(--theme-on-paywall-color);
    --theme-link-color: ${Luiv3Constants.baseColor['blue-600']};
    --theme-outline-color: ${Luiv3Constants.baseColor['gray-200']};
    --theme-soft-outline-color: ${Luiv3Constants.baseColor['gray-200']}80;
  `;

  return css`
    ${themeColors}
    ${themeElevatedColors}
    ${legacyColors}
  `;
};

type BaseColor = keyof ReturnType<
  typeof lightFromCorePalette | typeof darkFromCorePalette
>;

type getColorName<Color> = Color extends `on${infer UpperColorName}Container`
  ? Uncapitalize<UpperColorName>
  : never;

type CustomColorName = (typeof customColorDefs)[number]['name'];

type CustomColor =
  | `${CustomColorName}${'' | 'Container'}`
  | `on${Capitalize<CustomColorName>}${'' | 'Container'}`;

export type ColorName =
  | getColorName<BaseColor>
  | CustomColorName
  | 'inverseSurface';

export type Color = BaseColor | CustomColor;

type getBackgroundColor<TColor> = TColor extends `on${infer UpperColorName}`
  ? Uncapitalize<UpperColorName> extends Color
    ? Uncapitalize<UpperColorName>
    : never
  : never;

export type BackgroundColor = getBackgroundColor<Color> | 'inverseSurface';

interface GetColorVar {
  <T extends Color>(color: T): string;
  <T extends BackgroundColor>(color: T, elevation: Elevation): string;
}

export const colorVar: GetColorVar = (color: string, elevation?: Elevation) =>
  `var(--theme-color-${color}${
    elevation ? `-${`elevation-${elevation}`}` : ''
  })`;

export const isBackgroundColor = (color: Color): color is BackgroundColor =>
  !color.toLowerCase().includes('on') &&
  color !== 'outline' &&
  color !== 'divider' &&
  color !== 'shadow' &&
  color !== 'inversePrimary';

export const getOnColor = <T extends BackgroundColor>(color: T) =>
  color === 'inverseSurface'
    ? ('inverseOnSurface' as T extends 'inverseSurface'
        ? 'inverseOnSurface'
        : never)
    : (`on${capitalize(color)}` as T extends 'inverseSurface'
        ? never
        : `on${Capitalize<T>}`);
